import { getResponseStatusIfAxiosError, sessionAtom, userApi } from '@capsicum/common';
import { useMutation, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useAtomValue } from 'jotai';

import type {
	ApiErrorResponse,
	FetchUserMeAuthenticationMethodByMethodData,
	FetchUserMeAuthenticationMethodByMethodParams,
	FetchUserUserAuthenticationEventRequirementsByEventData,
	FetchUserUserAuthenticationEventRequirementsByEventParamsEvent,
	PostUserUserAuthenticationMethodConfigureByMethodData,
	PostUserUserAuthenticationMethodConfigureByMethodParamsMethod,
	PostUserUserAuthenticationMethodConfigureByMethodPayload,
	PostUserUserUserAuthenticationValidateData,
	PostUserUserUserAuthenticationValidatePayload,
	UserAuthenticationEventRequirement,
	UserAuthenticationMethod
} from '@capsicum/common';

export type UseGetUserAuthenticationMethodsByMethodQueryOptions = Omit<
	UseQueryOptions<
		FetchUserMeAuthenticationMethodByMethodData,
		AxiosError<ApiErrorResponse> | undefined,
		undefined | UserAuthenticationMethod
	>,
	'queryFn' | 'select'
>;
export const UseGetUserAuthenticationMethodsByMethodKeys = ['user-authentication-methods'];

export const useGetUserAuthenticationMethodsByMethod = (
	params: FetchUserMeAuthenticationMethodByMethodParams,
	options?: Omit<UseGetUserAuthenticationMethodsByMethodQueryOptions, 'queryFn' | 'queryKey'>
) => {
	const sessionTokens = useAtomValue(sessionAtom);

	return useQuery<
		FetchUserMeAuthenticationMethodByMethodData,
		AxiosError<ApiErrorResponse> | undefined,
		undefined | UserAuthenticationMethod
	>({
		enabled: !!sessionTokens?.authToken,
		queryFn: async () =>
			(await userApi).fetchUserMeAuthenticationMethodByMethod(
				{
					...params
				},
				{
					customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 404
				}
			),
		queryKey: [UseGetUserAuthenticationMethodsByMethodKeys, params],
		refetchOnWindowFocus: false,
		select: res => res.data,
		...options
	});
};

type UseCreateUserAuthenticationMethodPayload = {
	method: PostUserUserAuthenticationMethodConfigureByMethodParamsMethod;
	payload: PostUserUserAuthenticationMethodConfigureByMethodPayload;
};

const UseCreateUserAuthenticationMethodKey = ['create-user-authentication-method'];

export const useCreateUserAuthenticationMethod = () => {
	const { data, error, isPending, mutate } = useMutation<
		PostUserUserAuthenticationMethodConfigureByMethodData,
		AxiosError<ApiErrorResponse>,
		UseCreateUserAuthenticationMethodPayload,
		unknown
	>({
		mutationFn: async ({ method, payload }: UseCreateUserAuthenticationMethodPayload) =>
			(await userApi).postUserUserAuthenticationMethodConfigureByMethod(method, payload, {
				customErrorInterceptor: e => getResponseStatusIfAxiosError(e) === 400
			}),
		mutationKey: UseCreateUserAuthenticationMethodKey
	});

	return { data, error, isPending, mutate };
};

const UseValidateUserAuthenticationMethodKey = ['validate-user-authentication-method'];

export const useValidateUserAuthenticationMethod = () => {
	const { data, error, isPending, mutate } = useMutation<
		PostUserUserUserAuthenticationValidateData,
		AxiosError<ApiErrorResponse>,
		PostUserUserUserAuthenticationValidatePayload,
		unknown
	>({
		mutationFn: async (payload: PostUserUserUserAuthenticationValidatePayload) =>
			(await userApi).postUserUserUserAuthenticationValidate(payload, {
				customErrorInterceptor: e => {
					const errorCode = getResponseStatusIfAxiosError(e);

					return Boolean(errorCode && [400, 403].includes(errorCode));
				}
			}),
		mutationKey: UseValidateUserAuthenticationMethodKey
	});

	return { data, error, isPending, mutate };
};

type UseUpdateUserAuthenticationMethodPayload = {
	method: PostUserUserAuthenticationMethodConfigureByMethodParamsMethod;
	payload: PostUserUserAuthenticationMethodConfigureByMethodPayload;
};

const UseUpdateUserAuthenticationMethodKey = ['update-user-authentication-method'];

export const useUpdateUserAuthenticationMethod = () => {
	const { data, error, isPending, mutate } = useMutation<
		PostUserUserAuthenticationMethodConfigureByMethodData,
		AxiosError<ApiErrorResponse>,
		UseUpdateUserAuthenticationMethodPayload,
		unknown
	>({
		mutationFn: async ({ method, payload }: UseUpdateUserAuthenticationMethodPayload) =>
			(await userApi).postUserUserAuthenticationMethodConfigureByMethod(method, payload),
		mutationKey: UseUpdateUserAuthenticationMethodKey
	});

	return { data, error, isPending, mutate };
};

export type UseGetUserAuthenticationEventRequirementsQueryOptions = Omit<
	UseQueryOptions<
		FetchUserUserAuthenticationEventRequirementsByEventData,
		AxiosError<ApiErrorResponse> | undefined,
		undefined | UserAuthenticationEventRequirement
	>,
	'queryFn' | 'select'
>;
export const UseGetUserAuthenticationEventRequirementsKeys = ['user-authentication-event-requirements'];

export const useGetUserAuthenticationEventRequirements = (
	params: FetchUserUserAuthenticationEventRequirementsByEventParamsEvent,
	options?: Omit<UseGetUserAuthenticationEventRequirementsQueryOptions, 'queryFn' | 'queryKey'>
) => {
	const sessionTokens = useAtomValue(sessionAtom);

	return useQuery<
		FetchUserUserAuthenticationEventRequirementsByEventData,
		AxiosError<ApiErrorResponse> | undefined,
		undefined | UserAuthenticationEventRequirement
	>({
		enabled: !!sessionTokens?.authToken,
		queryFn: async () => (await userApi).fetchUserUserAuthenticationEventRequirementsByEvent(params),
		queryKey: [UseGetUserAuthenticationEventRequirementsKeys, params],
		select: res => res.data,
		...options
	});
};
